import {
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
} from 'react'
import { createPortal } from 'react-dom'
import { DialogDepthContext } from './dialogDepthContext'

export type FloatingMenuPlacement =
  | 'bottom-start'
  | 'bottom-end'
  | 'bottom-center'
  | 'top-start'
  | 'top-end'
  | 'top-center'
  | 'right-start'
  | 'left-start'

export type FloatingMenuProps = {
  /** Element the menu attaches to. The menu's outside-click handler treats this as
   *  part of the open surface. */
  anchorRef: RefObject<HTMLElement | null>
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  /** Preferred placement. Auto-flips to a fallback if the preferred placement
   *  overflows the viewport. */
  placement?: FloatingMenuPlacement
  /** Pixel gap between anchor and menu. */
  offset?: number
  /** Force the menu's min-width to match the anchor width. */
  matchAnchorWidth?: boolean
  /** Optional explicit min-width in px. Ignored if matchAnchorWidth is set. */
  minWidth?: number
  className?: string
  role?: 'menu' | 'listbox' | 'dialog'
  ariaLabel?: string
  style?: CSSProperties
}

const VIEWPORT_PADDING = 8

const PLACEMENT_FALLBACKS: Record<FloatingMenuPlacement, FloatingMenuPlacement[]> = {
  'bottom-start': ['bottom-start', 'bottom-end', 'top-start', 'top-end', 'right-start', 'left-start'],
  'bottom-end': ['bottom-end', 'bottom-start', 'top-end', 'top-start', 'right-start', 'left-start'],
  'bottom-center': ['bottom-center', 'top-center', 'bottom-start', 'top-start', 'right-start', 'left-start'],
  'top-start': ['top-start', 'top-end', 'bottom-start', 'bottom-end', 'right-start', 'left-start'],
  'top-end': ['top-end', 'top-start', 'bottom-end', 'bottom-start', 'right-start', 'left-start'],
  'top-center': ['top-center', 'bottom-center', 'top-start', 'bottom-start', 'right-start', 'left-start'],
  'right-start': ['right-start', 'left-start', 'bottom-start', 'top-start'],
  'left-start': ['left-start', 'right-start', 'bottom-end', 'top-end'],
}

type ContentSize = { width: number; height: number }

function computePosition(
  placement: FloatingMenuPlacement,
  anchor: DOMRect,
  content: ContentSize,
  offset: number,
): { left: number; top: number } {
  switch (placement) {
    case 'bottom-start':
      return { left: anchor.left, top: anchor.bottom + offset }
    case 'bottom-end':
      return { left: anchor.right - content.width, top: anchor.bottom + offset }
    case 'bottom-center':
      return { left: anchor.left + anchor.width / 2 - content.width / 2, top: anchor.bottom + offset }
    case 'top-start':
      return { left: anchor.left, top: anchor.top - content.height - offset }
    case 'top-end':
      return { left: anchor.right - content.width, top: anchor.top - content.height - offset }
    case 'top-center':
      return { left: anchor.left + anchor.width / 2 - content.width / 2, top: anchor.top - content.height - offset }
    case 'right-start':
      return { left: anchor.right + offset, top: anchor.top }
    case 'left-start':
      return { left: anchor.left - content.width - offset, top: anchor.top }
  }
}

// Width of the right-side nav rail (slim or expanded depending on pin state).
// Floating menus must clamp to (viewport - rail) so dropdowns never slip
// behind the rail when their anchor sits near the right edge of the page.
function railRightInset(): number {
  if (typeof window === 'undefined') return 0
  const raw = getComputedStyle(document.documentElement).getPropertyValue('--rail-current-width').trim()
  if (!raw) return 0
  const parsed = Number.parseFloat(raw)
  return Number.isFinite(parsed) ? parsed : 0
}

function bottomViewportInset(): number {
  if (typeof window === 'undefined') return 0
  const shell = document.querySelector('[data-member-shell]')
  if (shell) {
    const raw = getComputedStyle(shell).getPropertyValue('--member-bottom-chrome-height').trim()
    if (raw && raw.endsWith('rem')) {
      const rem = Number.parseFloat(raw)
      const rootFs = Number.parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
      const hasBoardFilter = Boolean(document.querySelector('.memberBoardFilterBar'))
      const filterBarExtra = hasBoardFilter ? 48 : 0
      return (rem * rootFs) + filterBarExtra
    }
  }
  return 0
}

function overflowAmount(left: number, top: number, content: ContentSize): number {
  const innerWidth = window.innerWidth
  const innerHeight = window.innerHeight
  const rightEdge = innerWidth - railRightInset()
  const bottomEdge = innerHeight - bottomViewportInset()
  const overflowLeft = Math.max(0, VIEWPORT_PADDING - left)
  const overflowRight = Math.max(0, left + content.width - (rightEdge - VIEWPORT_PADDING))
  const overflowTop = Math.max(0, VIEWPORT_PADDING - top)
  const overflowBottom = Math.max(0, top + content.height - (bottomEdge - VIEWPORT_PADDING))
  return overflowLeft + overflowRight + overflowTop + overflowBottom
}

/**
 * Portal-based floating menu primitive used by every dropdown / popover in the
 * app. The menu renders into document.body, so ancestor `overflow: hidden`,
 * `transform`, or stacking-context rules never clip it. It auto-flips across
 * up to six placements to find a fitting direction and never sets an internal
 * `max-height` — if no placement fits, it overflows the viewport rather than
 * creating an internal scroll region (per product spec).
 */
export const FloatingMenu = ({
  anchorRef,
  isOpen,
  onClose,
  children,
  placement = 'bottom-start',
  offset = 6,
  matchAnchorWidth = false,
  minWidth,
  className,
  role = 'menu',
  ariaLabel,
  style,
}: FloatingMenuProps) => {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null)
  const [anchorWidth, setAnchorWidth] = useState<number | null>(null)

  useLayoutEffect(() => {
    if (!isOpen) return
    const updatePosition = () => {
      const anchor = anchorRef.current
      const content = contentRef.current
      if (!anchor || !content) return
      const anchorRect = anchor.getBoundingClientRect()
      const contentSize: ContentSize = { width: content.offsetWidth, height: content.offsetHeight }
      setAnchorWidth(anchorRect.width)
      const candidates = PLACEMENT_FALLBACKS[placement]
      let chosen: { left: number; top: number } | null = null
      let chosenOverflow = Infinity
      for (const candidate of candidates) {
        const pos = computePosition(candidate, anchorRect, contentSize, offset)
        const overflow = overflowAmount(pos.left, pos.top, contentSize)
        if (overflow === 0) {
          chosen = pos
          break
        }
        if (overflow < chosenOverflow) {
          chosenOverflow = overflow
          chosen = pos
        }
      }
      if (chosen) {
        const rightEdge = window.innerWidth - railRightInset()
        const bottomEdge = window.innerHeight - bottomViewportInset()
        const clampedLeft = Math.max(VIEWPORT_PADDING, Math.min(chosen.left, rightEdge - contentSize.width - VIEWPORT_PADDING))
        const clampedTop = Math.max(VIEWPORT_PADDING, Math.min(chosen.top, Math.max(VIEWPORT_PADDING, bottomEdge - contentSize.height - VIEWPORT_PADDING)))
        setPosition({ left: clampedLeft, top: clampedTop })
      }
    }
    updatePosition()

    // Coalesce bursts of resize/scroll events (e.g. the soft keyboard
    // animating open/closed fires several in quick succession) into a
    // single recompute per frame, so this never thrashes layout.
    let rafId: number | null = null
    const scheduleUpdate = () => {
      if (rafId != null) return
      rafId = requestAnimationFrame(() => {
        rafId = null
        updatePosition()
      })
    }

    window.addEventListener('resize', scheduleUpdate)
    window.addEventListener('scroll', updatePosition, true)
    // visualViewport reflects the soft-keyboard-adjusted viewport on mobile —
    // window's own resize/scroll events don't fire when only the keyboard
    // opens/closes, so an anchor's on-screen position can go stale without
    // this. Purely additive: it only ever schedules another call to the same
    // `updatePosition`, so it can only make an open menu's position *more*
    // correct for every existing consumer, never change placement logic.
    const visualViewport = typeof window !== 'undefined' ? window.visualViewport : null
    visualViewport?.addEventListener('resize', scheduleUpdate)
    visualViewport?.addEventListener('scroll', scheduleUpdate)
    const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(updatePosition) : null
    if (contentRef.current && resizeObserver) resizeObserver.observe(contentRef.current)
    return () => {
      window.removeEventListener('resize', scheduleUpdate)
      window.removeEventListener('scroll', updatePosition, true)
      visualViewport?.removeEventListener('resize', scheduleUpdate)
      visualViewport?.removeEventListener('scroll', scheduleUpdate)
      resizeObserver?.disconnect()
      if (rafId != null) cancelAnimationFrame(rafId)
    }
  }, [isOpen, placement, offset, anchorRef])

  useEffect(() => {
    if (!isOpen) return
    const onPointer = (event: PointerEvent) => {
      const target = event.target instanceof Node ? event.target : null
      if (!target) return
      const anchor = anchorRef.current
      const content = contentRef.current
      if (anchor && anchor.contains(target)) return
      if (content && content.contains(target)) return
      event.stopPropagation()
      onClose()
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
      }
    }
    window.addEventListener('pointerdown', onPointer, true)
    window.addEventListener('keydown', onKey, true)
    return () => {
      window.removeEventListener('pointerdown', onPointer, true)
      window.removeEventListener('keydown', onKey, true)
    }
  }, [isOpen, onClose, anchorRef])

  const [prevIsOpen, setPrevIsOpen] = useState(isOpen)
  if (prevIsOpen !== isOpen) {
    setPrevIsOpen(isOpen)
    if (!isOpen) {
      setPosition(null)
    }
  }

  const dialogDepth = useContext(DialogDepthContext)

  if (!isOpen) return null

  const menuZIndex: CSSProperties['zIndex'] =
    dialogDepth > 1
      ? `calc(var(--z-popover) + ${(dialogDepth - 1) * 20})`
      : 'var(--z-popover)'

  const widthHint: CSSProperties = matchAnchorWidth && anchorWidth != null
    ? { minWidth: `${anchorWidth}px` }
    : minWidth != null
      ? { minWidth: `${minWidth}px` }
      : {}

  const computedStyle: CSSProperties = {
    position: 'fixed',
    left: position?.left ?? 0,
    top: position?.top ?? 0,
    // Neutralize anchored offsets baked into reused class names (e.g.
    // `.profileDropdown { right: 0 }`, `.breadcrumbDropdown { top: calc(100% + 6px) }`).
    // With `position: fixed` + both `left` and `right` set, the box stretches
    // across the viewport — which is what was hiding the menus.
    right: 'auto',
    bottom: 'auto',
    margin: 0,
    visibility: position ? 'visible' : 'hidden',
    // Must sit above drawer and dialog overlays via shared layer scale
    // (`--z-popover` / `--z-dialog-menu`). Reads DialogDepthContext so menus
    // opened inside nested dialogs stack strictly above that dialog level.
    zIndex: menuZIndex,
    ...widthHint,
    ...style,
  }

  return createPortal(
    <div
      ref={contentRef}
      className={className}
      role={role}
      aria-label={ariaLabel}
      data-floating-menu-root="true"
      data-breadcrumb-menu-root="true"
      style={computedStyle}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    document.body,
  )
}
